import React, { useContext, useEffect, useMemo, useReducer, useState } from "react";
import { toast } from "react-toastify";

import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import { DeleteOutline, Edit, Search } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";

import MainContainer from "../../components/MainContainer";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import QueueModal from "../../components/QueueModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import ForbiddenPage from "../../components/ForbiddenPage";

import MetricsStrip from "../../components/MetricsStrip";

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    minWidth: 0,
  },
  contentStack: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    width: "100%",
    minWidth: 0,
  },
  hero: {
    width: "100%",
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  heroActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      "& > *": {
        flex: "1 1 100%",
      },
    },
  },
  heroTitle: {
    fontWeight: 600,
    letterSpacing: "-0.01em",
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: theme.spacing(1.5),
    width: "100%",
    minWidth: 0,
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
  },
  summaryCard: {
    padding: theme.spacing(1.5, 1.75),
    borderRadius: 16,
    minHeight: 92,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
    backgroundColor: theme.palette.background.paper,
  },
  summaryLabel: {
    fontSize: "0.74rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    opacity: 0.7,
  },
  summaryValue: {
    fontSize: "1.5rem",
    fontWeight: 700,
    lineHeight: 1.1,
    marginTop: theme.spacing(0.75),
  },
  filtersPaper: {
    width: "100%",
    minWidth: 0,
    padding: theme.spacing(1.5),
    borderRadius: 18,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  filtersHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  filtersTitle: {
    fontWeight: 600,
  },
  filtersActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "stretch",
      "& > *": {
        flex: "1 1 100%",
      },
    },
  },
  tableSearch: {
    width: 320,
    maxWidth: "100%",
  },
  tablePaper: {
    borderRadius: 18,
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  tableHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(1.25, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "flex-start",
    },
  },
  tableHeaderTitle: {
    fontWeight: 600,
  },
  tableContainer: {
    width: "100%",
    minWidth: 0,
    maxHeight: "68vh",
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  tableHeadCell: {
    fontWeight: 600,
  },
  queueNameCell: {
    minWidth: 220,
  },
  queueNameContent: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
  },
  colorSwatch: {
    width: 14,
    height: 14,
    borderRadius: 999,
    border: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
  },
  queueMeta: {
    minWidth: 0,
  },
  queueName: {
    fontWeight: 600,
  },
  queueSubtext: {
    opacity: 0.72,
  },
  configCell: {
    display: "flex",
    justifyContent: "center",
  },
  configStack: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(0.75),
    flexWrap: "wrap",
  },
  greetingText: {
    maxWidth: 320,
  },
  actionsCell: {
    whiteSpace: "nowrap",
  },
  helperText: {
    opacity: 0.72,
    marginTop: theme.spacing(0.5),
  },
}));

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_QUEUES": {
      const queues = action.payload;
      const newQueues = [];

      queues.forEach((queue) => {
        const queueIndex = state.findIndex((item) => item.id === queue.id);

        if (queueIndex !== -1) {
          state[queueIndex] = queue;
        } else {
          newQueues.push(queue);
        }
      });

      return [...state, ...newQueues];
    }
    case "UPDATE_QUEUES": {
      const queue = action.payload;
      const queueIndex = state.findIndex((item) => item.id === queue.id);

      if (queueIndex !== -1) {
        state[queueIndex] = queue;
        return [...state];
      }

      return [queue, ...state];
    }
    case "DELETE_QUEUE": {
      const queueId = action.payload;
      return state.filter((item) => item.id !== queueId);
    }
    case "RESET":
      return [];
    default:
      return state;
  }
};

const Queues = () => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);
  const companyId = user.companyId;

  const [queues, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);
  const [reloadFlag, setReloadFlag] = useState(0);
  const [searchParam, setSearchParam] = useState("");
  const [queueModalOpen, setQueueModalOpen] = useState(false);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  useEffect(() => {
    const fetchQueues = async () => {
      setLoading(true);

      try {
        dispatch({ type: "RESET" });
        const { data } = await api.get("/queue");
        dispatch({ type: "LOAD_QUEUES", payload: data || [] });
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchQueues();
  }, [reloadFlag]);

  useEffect(() => {
    const onQueueEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_QUEUES", payload: data.queue });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_QUEUE", payload: data.queueId });
      }
    };

    socket.on(`company-${companyId}-queue`, onQueueEvent);

    return () => {
      socket.off(`company-${companyId}-queue`, onQueueEvent);
    };
  }, [socket, companyId]);

  const refreshQueues = () => {
    setReloadFlag((current) => current + 1);
  };

  const handleOpenQueueModal = () => {
    setQueueModalOpen(true);
    setSelectedQueue(null);
  };

  const handleCloseQueueModal = () => {
    setQueueModalOpen(false);
    setSelectedQueue(null);
  };

  const handleEditQueue = (queue) => {
    setSelectedQueue(queue);
    setQueueModalOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmModalOpen(false);
    setSelectedQueue(null);
  };

  const handleDeleteQueue = async () => {
    if (!selectedQueue) return;

    try {
      await api.delete(`/queue/${selectedQueue.id}`);
      dispatch({ type: "DELETE_QUEUE", payload: selectedQueue.id });
      toast.success(i18n.t("queues.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }

    setSelectedQueue(null);
    setConfirmModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const filteredQueues = useMemo(() => {
    if (!searchParam) {
      return queues;
    }

    return queues.filter((queue) => {
      const name = String(queue.name || "").toLowerCase();
      const greeting = String(queue.greetingMessage || "").toLowerCase();
      const order = String(queue.orderQueue || "").toLowerCase();

      return (
        name.includes(searchParam) ||
        greeting.includes(searchParam) ||
        order.includes(searchParam)
      );
    });
  }, [queues, searchParam]);

  const summary = useMemo(() => {
    const withGreeting = queues.filter((queue) => Boolean(queue.greetingMessage)).length;
    const withOrder = queues.filter((queue) => Boolean(queue.orderQueue)).length;
    const withBot = queues.filter(
      (queue) => Array.isArray(queue.chatbots) && queue.chatbots.length > 0
    ).length;

    return {
      total: queues.length,
      withGreeting,
      withOrder,
      withBot,
      visible: filteredQueues.length,
    };
  }, [filteredQueues.length, queues]);

  const summaryCards = useMemo(
    () => [
      { key: "queues", label: "Filas", value: summary.total },
      { key: "greeting", label: "Com saudacao", value: summary.withGreeting },
      { key: "order", label: "Com ordem", value: summary.withOrder },
      { key: "bot", label: "Com bot", value: summary.withBot },
    ],
    [summary]
  );


  if (user.profile === "user") {
    return (
      <MainContainer className={classes.mainContainer} fullWidth>
        <ForbiddenPage />
      </MainContainer>
    );
  }

  return (
    <MainContainer className={classes.mainContainer} fullWidth>
      <ConfirmationModal
        title={
          selectedQueue
            ? `${i18n.t("queues.confirmationModal.deleteTitle")} ${selectedQueue.name}?`
            : i18n.t("queues.confirmationModal.deleteTitle")
        }
        open={confirmModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={handleDeleteQueue}
      >
        {i18n.t("queues.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <QueueModal
        open={queueModalOpen}
        onClose={handleCloseQueueModal}
        queueId={selectedQueue?.id}
        onEdit={(res) => {
          if (res) {
            setTimeout(() => {
              handleEditQueue(res);
            }, 500);
          }
        }}
      />

      <div className={classes.contentStack}>
        <Box className={classes.hero}>
          <Box>
            <Typography variant="h5" className={classes.heroTitle}>
              {i18n.t("queues.title")}
            </Typography>
          </Box>
          <Box className={classes.heroActions}>
            <Button variant="outlined" onClick={refreshQueues} disabled={loading}>
              Atualizar
            </Button>
            <Button variant="contained" color="primary" onClick={handleOpenQueueModal}>
              {i18n.t("queues.buttons.add")}
            </Button>
          </Box>
        </Box>

        <MetricsStrip ariaLabel="Indicadores de filas" items={summaryCards} />

        <Paper className={classes.tablePaper} variant="outlined">
          <Box className={classes.tableHeader}>
            <Typography variant="body1" className={classes.tableHeaderTitle}>
              Filas operacionais
            </Typography>
            <Box display="flex" alignItems="center" flexWrap="wrap" gridGap={8}>
              <TextField
                className={classes.tableSearch}
                variant="outlined"
                size="small"
                placeholder={i18n.t("contacts.searchPlaceholder")}
                type="search"
                value={searchParam}
                onChange={handleSearch}
                inputProps={{ "aria-label": "Pesquisar filas" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <Chip size="small" label={`${summary.visible} visiveis`} />
              <Chip size="small" label={`${summary.withBot} com bot`} />
            </Box>
          </Box>

          <TableContainer className={classes.tableContainer}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="center" className={classes.tableHeadCell}>
                    {i18n.t("queues.table.ID")}
                  </TableCell>
                  <TableCell className={classes.tableHeadCell}>
                    {i18n.t("queues.table.name")}
                  </TableCell>
                  <TableCell align="center" className={classes.tableHeadCell}>
                    Configuracao
                  </TableCell>
                  <TableCell align="center" className={classes.tableHeadCell}>
                    {i18n.t("queues.table.greeting")}
                  </TableCell>
                  <TableCell align="center" className={classes.tableHeadCell}>
                    {i18n.t("queues.table.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredQueues.map((queue) => (
                  <TableRow key={queue.id} hover>
                    <TableCell align="center">{queue.id}</TableCell>
                    <TableCell className={classes.queueNameCell}>
                      <Box className={classes.queueNameContent}>
                        <span
                          className={classes.colorSwatch}
                          style={{ backgroundColor: queue.color || "#d1d5db" }}
                        />
                        <Box className={classes.queueMeta}>
                          <Typography variant="body2" className={classes.queueName}>
                            {queue.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            className={classes.queueSubtext}
                            noWrap
                          >
                            {queue.color || "Sem cor definida"}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center" className={classes.configCell}>
                      <Box className={classes.configStack}>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`Ordem: ${queue.orderQueue || "--"}`}
                        />
                        <Chip
                          size="small"
                          color={
                            Array.isArray(queue.chatbots) && queue.chatbots.length > 0
                              ? "primary"
                              : "default"
                          }
                          variant={
                            Array.isArray(queue.chatbots) && queue.chatbots.length > 0
                              ? "default"
                              : "outlined"
                          }
                          label={
                            Array.isArray(queue.chatbots) && queue.chatbots.length > 0
                              ? `${queue.chatbots.length} bots`
                              : "Sem bot"
                          }
                        />
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" noWrap className={classes.greetingText}>
                        {queue.greetingMessage || "--"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" className={classes.actionsCell}>
                      <IconButton size="small" onClick={() => handleEditQueue(queue)}>
                        <Edit />
                      </IconButton>

                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedQueue(queue);
                          setConfirmModalOpen(true);
                        }}
                      >
                        <DeleteOutline />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}

                {loading && <TableRowSkeleton columns={5} />}
              </TableBody>
            </Table>
          </TableContainer>

          {!loading && filteredQueues.length === 0 && (
            <Box p={4} textAlign="center">
              <Typography variant="h6">Nenhuma fila encontrada</Typography>
              <Typography variant="body2" className={classes.helperText}>
                Ajuste a busca ou cadastre uma nova fila para continuar.
              </Typography>
            </Box>
          )}
        </Paper>
      </div>
    </MainContainer>
  );
};

export default Queues;
